import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { renameBookmark, RenameBookmarkModel } from "client/Api/Documents/Bookmarks/bookmarks";
import { ReactElement, useState } from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";

type RenameBookmarkFormModel = Omit<RenameBookmarkModel, "id">;

const validationConfiguration: IValidationConfiguration<RenameBookmarkFormModel> = createValidationConfiguration<RenameBookmarkFormModel>();
validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type RenameBookmarkDialogProps = {
	id: number;
	initialName: string;
	onClose(): void;
	onRenamed(
		id: number,
		newName: string
	): void;
};

export const RenameBookmarkDialog = (props: RenameBookmarkDialogProps): ReactElement => {
	const {
		id,
		initialName,
		onClose,
		onRenamed
	} = props;

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [model, setModel] = useState<RenameBookmarkFormModel>({name: initialName});
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const formHandler: IFormHandler<RenameBookmarkFormModel> = useFormHandler(
		model,
		setModel,
		validationConfiguration
	);

	const submitHandler = async(): Promise<void> => {
		if (isSubmitting) {
			return;
		}
		setIsSubmitting(true);
		const validationResult: InputValidationResult = await formHandler.validation
			.validate(globalCancellation);
		if (validationResult.kind === InputValidationResultKind.Invalid) {
			setIsSubmitting(false);
			return;
		}

		try {
			const newData: RenameBookmarkModel = {
				id,
				name: model.name
			};
			await renameBookmark(newData, globalCancellation);
			onRenamed(
				id,
				model.name
			);
			setIsSubmitting(false);
			onClose();
		} catch (e){
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog
			trackingId="rename-bookmark-dialog"
			hasBusyIndicator={isSubmitting}
		>
			<Dialog.Header
				title={localize("_RenameBookmark")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form
					onSubmit={submitHandler}
				>
					<FormItem>
						<TextInput
							{...formHandler.for("name").attachToTextInput()}
							autoFocus="select"
						/>
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={submitHandler}
						text={localize("_Rename")}
						trackingId="confirm"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

RenameBookmarkDialog.displayName = "RenameBookmarkDialog";